import { useContext } from 'react'

import {
  Button, Form, Link, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, sortArrayBy } from '@domain-group/javascript-utils'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { adminMemberRoles } from '@functions/teamMember'

import useCreateOrEditAdminTeamMemberModal from './hooks/useCreateOrEditAdminTeamMemberModal'

const MODAL_KEY = 'CreateOrEditAdminTeamMemberModal'

const renderAssignRolesLabel = (manageUserTeamMemberRolesUrl: string) => (
  <Link
    href={manageUserTeamMemberRolesUrl}
    textProps={{ fontSize: 'xsmall' }}
  >
    Assign Roles
  </Link>
)

const renderOptions = (
  group: 'team_lead' | 'support',
  allowedTeamMemberRoles: typeof adminMemberRoles,
) => {
  const filteredOptions = allowedTeamMemberRoles.filter(memberRole => memberRole.group === group)
  const sorted = sortArrayBy(filteredOptions, 'asc', 'title')

  return sorted.map((entity) => {
    const { key, title } = entity

    return (
      <option key={key} value={`${key}.${group}`}>
        {title}
      </option>
    )
  })
}

type ModalCallbacks = {
  closeModal: () => void,
  createTeamMember: (payload: HandleCallbackActionParams) => void,
  updateTeamMember: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditAdminTeamMemberModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createTeamMember, updateTeamMember } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const teamMember = digObject(modalPayload, 'teamMember', {})

  const newRecord = !teamMember.id

  const {
    allowedTeamMemberRoles,
    callbacks: {
      createTeamMember: createFn,
      updateTeamMember: updateFn,
    },
    creating,
    filteredTeamMembers,
    hasSupportRoles,
    hasTeamLeadRoles,
    teamMemberFormPayload,
    updating,
    urls: {
      manageUserTeamMemberRolesUrl,
    },
  } = useCreateOrEditAdminTeamMemberModal(teamMember)

  const {
    entityState,
    entityState: {
      member_role,
      team_lead,
      user_id,
    },
    errors,
    saveEnabled,
    setEntityState,
  } = teamMemberFormPayload

  const createOrUpdateTeamMemberPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Team Member ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Team Member" />

      <SidebarModal.Content>
        <Form>
          <EntitySelector
            callbacks={{
              // @ts-expect-error TS2322
              selectItem: (user: { id: number }) => {
                setEntityState({ user_id: user?.id })
              },
            }}
            entityKey="users"
            errorMessage={errors.user_id}
            label="* User"
            searchRequestOptions={{ admin_users_only: true }}
            selectedIds={filteredTeamMembers.map(member => member.user_id)}
            selectedItemId={user_id}
          />

          <Form.Field
            label="Role"
            labelRight={!allowedTeamMemberRoles.length
              && !!entityState.user_id
              && renderAssignRolesLabel(manageUserTeamMemberRolesUrl)}
            marginTop="large"
          >
            <select
              disabled={!allowedTeamMemberRoles.length}
              name="member_role"
              value={`${member_role}.${team_lead ? 'team_lead' : 'support'}`}
              onChange={(e) => {
                const [value, group] = e.target.value.split('.')
                setEntityState({ member_role: value, team_lead: group === 'team_lead' })
              }}
            >
              <option value="">Please Select...</option>
              {hasTeamLeadRoles && (
                <optgroup label="Team Lead">
                  {renderOptions('team_lead', allowedTeamMemberRoles)}
                </optgroup>
              )}

              {hasSupportRoles && (
                <optgroup label="Support">
                  {renderOptions('support', allowedTeamMemberRoles)}
                </optgroup>
              )}
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createTeamMember(createOrUpdateTeamMemberPayload)
              : () => updateTeamMember(createOrUpdateTeamMemberPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Team Member` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAdminTeamMemberModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
